"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Send } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqs = [{
  question: "Hukuki danışmanlık ücretli midir?",
  answer: "Evet. 1136 sayılı Avukatlık Kanunu uyarınca avukatlık ücreti, avukat tarafından sunulan hukuki yardımın karşılığıdır. Danışmanlık ücretleri yürürlükteki Avukatlık Asgari Ücret Tarifesi ve talebin kapsamı dikkate alınarak belirlenir. Randevu talebi oluşturulması tek başına hukuki danışmanlık hizmetinin başlaması anlamına gelmez."
}, {
  question: "Randevu nasıl oluşturulur?",
  answer: "İletişim formunu doldurmanızın ardından büromuz tarafınızla telefon yoluyla iletişime geçer. Formun gönderilmiş olması tek başına randevunun kesinleştiği anlamına gelmez."
}, {
  question: "Formu göndermem avukat–müvekkil ilişkisi kurar mı?",
  answer: "Hayır. İletişim formunun gönderilmesi veya ön görüşme yapılması tek başına avukatlık ya da vekâlet ilişkisi kurmaz. Avukatlık hizmetinin üstlenilmesi ayrıca kararlaştırılır."
}, {
  question: "Form üzerinden belge gönderebilir miyim?",
  answer: "Bu form belge gönderimi amacıyla kullanılmamaktadır. İlk iletişim sonrasında gerekli görülmesi halinde belgelerin iletilebileceği uygun yöntem tarafınıza bildirilir."
}, {
  question: "Mesajımda hangi bilgilere yer vermeliyim?",
  answer: "İletişim veya randevu talebinizin konusunu kısaca açıklamanız yeterlidir. İlk aşamada gerekli olmayan kişisel verileri, özel nitelikli kişisel verileri veya üçüncü kişilere ait bilgileri paylaşmamanız önerilir."
}, {
  question: "Süreye tabi veya acil bir hukuki işlemim varsa ne yapmalıyım?",
  answer: "İletişim formunun hemen inceleneceği varsayılmamalıdır. Kanuni veya usulî süreye tabi işlemlerde yalnızca form gönderimine dayanılmaması; gerekli hâllerde büroyla telefon yoluyla doğrudan iletişim kurulması önerilir."
}];
function ContactFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [formValues, setFormValues] = React.useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [hasKvkkInfoConsent, setHasKvkkInfoConsent] = React.useState(false);
  const [hasTermsConsent, setHasTermsConsent] = React.useState(false);
  const cleanPhone = formValues.phone.replace(/[\s()-]/g, "");
  const isPhoneValid = /^(\+90|0)?5\d{9}$/.test(cleanPhone);
  const isFormValid = formValues.name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email.trim()) && isPhoneValid && formValues.message.trim().length >= 10 && hasKvkkInfoConsent && hasTermsConsent;
  if (isSubmitted && !submitError) {
    return <div className="space-y-6">
        <Alert className="border-border bg-card text-card-foreground">
          <CheckCircle2 className="size-5 text-foreground" />
          <AlertTitle className="font-default text-lg font-semibold tracking-normal">
            Mesajınız İletildi
          </AlertTitle>
          <AlertDescription className="text-muted-foreground mt-2">
            {successMessage || "Talebiniz başarıyla alınmıştır. İnceleme sonrası en kısa sürede tarafınıza dönüş sağlanacaktır."}
          </AlertDescription>
        </Alert>
        <div className="pt-2 text-center">
          <Button type="button" variant="outline" onClick={() => {
          setFormValues({
            name: "",
            email: "",
            phone: "",
            message: ""
          });
          setHasKvkkInfoConsent(false);
          setHasTermsConsent(false);
          resetForm();
        }} className="border-border text-foreground hover:bg-muted">
            Yeni Mesaj Gönder
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <input type="hidden" name="kvkk_bilgilendirme" value="Evet" />
      <input type="hidden" name="iletisim_formu_kullanim_kosullari" value="Kabul edildi" />
      <input type="hidden" name="gonderim_tarihi_saati" value={new Date().toISOString()} />

      {submitError && <Alert variant="destructive" className="border-destructive/50">
          <AlertCircle className="size-5" />
          <AlertTitle className="font-default text-base font-medium">
            Gönderim Başarısız Oldu
          </AlertTitle>
          <AlertDescription>
            {submitError || "Bir hata meydana geldi. Lütfen tekrar deneyiniz."}
          </AlertDescription>
        </Alert>}

      <FormField control={control} name="name" rules={{
      required: "Ad ve soyad alanı zorunludur",
      minLength: {
        value: 2,
        message: "Ad soyad en az 2 karakter olmalıdır"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium tracking-normal">
              Ad Soyad <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Input placeholder="Adınız ve Soyadınız" {...field} value={field.value ?? ""} onChange={e => {
          field.onChange(e);
          setFormValues(prev => ({
            ...prev,
            name: e.target.value
          }));
        }} className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-ring px-3.5 py-2.5 h-11" />
            </FormControl>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "E-posta alanı zorunludur",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Geçerli bir e-posta adresi giriniz"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium tracking-normal">
              E-posta <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="ornek@alanadi.com" {...field} value={field.value ?? ""} onChange={e => {
          field.onChange(e);
          setFormValues(prev => ({
            ...prev,
            email: e.target.value
          }));
        }} className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-ring px-3.5 py-2.5 h-11" />
            </FormControl>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Telefon alanı zorunludur",
      pattern: {
        value: /^(\+90|0)?5\d{9}$/,
        message: "Geçerli bir telefon numarası giriniz (örn: 05XX XXX XX XX)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium tracking-normal">
              Telefon <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="05XX XXX XX XX" {...field} value={field.value ?? ""} onChange={e => {
          field.onChange(e);
          setFormValues(prev => ({
            ...prev,
            phone: e.target.value
          }));
        }} className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-ring px-3.5 py-2.5 h-11" />
            </FormControl>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Mesaj alanı zorunludur",
      minLength: {
        value: 10,
        message: "Mesajınız en az 10 karakter içermelidir"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium tracking-normal">
              Mesaj <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Konu hakkında özet bilgi veya iletmek istediğiniz detaylar..." {...field} value={field.value ?? ""} onChange={e => {
          field.onChange(e);
          setFormValues(prev => ({
            ...prev,
            message: e.target.value
          }));
        }} className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-ring px-3.5 py-3 min-h-32 resize-y" />
            </FormControl>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

      <div className="space-y-3 pt-2">
        <div className="flex items-start gap-3">
          <input type="checkbox" id="contact-kvkk-info" checked={hasKvkkInfoConsent} onChange={e => setHasKvkkInfoConsent(e.target.checked)} className="mt-1 size-4 rounded-sm border-border bg-transparent text-primary accent-primary focus:ring-ring focus:ring-offset-0 cursor-pointer" />
          <label htmlFor="contact-kvkk-info" className="text-xs leading-relaxed text-muted-foreground select-none cursor-pointer">
            <Link to="/ContactFormPrivacyNoticePage" className="text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors">
              İletişim Formu Aydınlatma Metni
            </Link>
            {"'ni ve "}
            <Link to="/PersonalDataProtectionAndProcessingPolicyPage" className="text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors">
              KVKK ve Gizlilik Politikası
            </Link>
            {"'nı okudum; kişisel verilerimin işlenmesine ilişkin bilgilendirildim."}
          </label>
        </div>

        <div className="flex items-start gap-3">
          <input type="checkbox" id="contact-terms" checked={hasTermsConsent} onChange={e => setHasTermsConsent(e.target.checked)} className="mt-1 size-4 rounded-sm border-border bg-transparent text-primary accent-primary focus:ring-ring focus:ring-offset-0 cursor-pointer" />
          <label htmlFor="contact-terms" className="text-xs leading-relaxed text-muted-foreground select-none cursor-pointer">
            <Link to="/ContactFormTermsOfUsePage" className="text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors">
              İletişim Formu Kullanım Koşulları
            </Link>
            {"'nı okudum ve kabul ediyorum."}
          </label>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed pt-1">
          Bu form üzerinden iletişim kurulması veya randevu talebinde bulunulması tek başına avukatlık ya da vekâlet ilişkisi kurmaz. Zorunlu olmadıkça özel nitelikli kişisel verilerinizi veya üçüncü kişilere ait kişisel verileri form üzerinden paylaşmayınız.
        </p>
      </div>

      <Button type="submit" disabled={!isFormValid || isSubmitting} className="w-full h-12 bg-primary text-primary-foreground font-medium text-base hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        {isSubmitting ? <span className="inline-flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            İletiliyor...
          </span> : <span className="inline-flex items-center gap-2">
            <Send className="size-4" />
            Mesaj Gönder
          </span>}
      </Button>
    </Form>;
}
export default function Contact() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="contactform" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="text-center mb-12 lg:mb-16">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">
              İLETİŞİM
            </span>
            <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-normal text-foreground tracking-tight">
              İletişim
            </h1>
            <div className="w-12 h-px bg-border mx-auto mt-4 mb-4" />
            <p className="text-muted-foreground text-sm sm:text-base font-sans max-w-xl mx-auto">
              İletişim ve randevu talepleriniz için aşağıdaki formu doldurunuz. Büromuz sizinle irtibata geçecektir.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-[0.58fr_0.42fr] gap-8 lg:gap-12 items-start">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: "easeOut"
          }} className="bg-card text-card-foreground border border-border p-6 sm:p-10 shadow-xs">
              <FormProvider formId="contact_form_contact_page" sectionName="contact-form-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: "",
              kvkk_bilgilendirme: "Evet",
              iletisim_formu_kullanim_kosullari: "Kabul edildi",
              gonderim_tarihi_saati: new Date().toISOString()
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "^(\\+90|0)?5\\d{9}$"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Mesaj Gönder" successMessage="Mesajınız başarıyla iletildi. En kısa sürede sizinle iletişime geçeceğiz.">
                <ContactFormFields />
              </FormProvider>
            </motion.div>

            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2,
            ease: "easeOut"
          }} className="bg-card text-card-foreground border border-border p-6 sm:p-8 shadow-xs">
              <div className="mb-6">
                <h2 className="font-default text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  Sıkça Sorulan Sorular
                </h2>
                <div className="w-8 h-px bg-border mt-3" />
              </div>

              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border py-1">
                    <AccordionTrigger className="font-default font-semibold text-foreground text-base sm:text-lg hover:no-underline hover:text-muted-foreground transition-colors text-left py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-sans text-muted-foreground text-sm sm:text-base leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}